'use client';

import { PieceFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import React, { useState } from 'react';
import { useTranslations } from 'next-intl';

export default function Tooltip({ tooltip }: { tooltip: PieceFragment | null | undefined }) {
  const t = useTranslations('Tooltip');
  const [isOpen, setIsOpen] = useState(false);

  const titleContent = tooltip?.components?.[0]?.content;
  const title = componentByTypename(titleContent, 'RichTextContent');
  const paragraphsContent = tooltip?.components?.[1].content;
  const paragraphs = componentByTypename(paragraphsContent, 'ContentChunkContent');

  if (!paragraphs) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="relative flex h-[20px] w-[20px] rounded-full border border-primary-100 text-sm font-bold"
        aria-label={t('moreInfo')}
      >
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" aria-hidden="true">?</span>
      </button>
      <Dialog open={isOpen} className="relative z-40" onClose={() => setIsOpen(false)}>
        {/* Overlay */}
        <div className="fixed inset-0 bg-neutral-100/20" />
        <Dialog.Panel className="fixed left-1/2 top-1/2 h-fit max-h-[90vh] w-[95vw] -translate-x-1/2 -translate-y-1/2 transform overflow-y-auto rounded-xl bg-white p-6 px-4 py-10 shadow-lg sm:px-10 lg:w-full lg:max-w-5xl">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="absolute right-5 top-5 rounded-xl p-1 duration-200 ease-in hover:bg-neutral-10"
            aria-label={t('close')}
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="mx-auto max-w-3xl">
            <Dialog.Title>
              <RichTextRenderer className="text-2xl sm:text-3xl" blocks={title} />
            </Dialog.Title>
            <Dialog.Description>
              {paragraphs?.chunks.map((chunk, i) => {
                const title = extractComponentFromChunk(chunk, 'title', 'RichTextContent');
                const description = extractComponentFromChunk(
                  chunk,
                  'description',
                  'RichTextContent',
                );
                return (
                  <div className="mt-8 space-y-2">
                    <RichTextRenderer blocks={title} className="text-xl sm:text-2xl" />
                    <RichTextRenderer blocks={description} className="sm:text-md" />
                  </div>
                );
              })}
            </Dialog.Description>
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  );
}
